import { Fragment, useEffect, useMemo, useState } from "react"

import { Divider, Tooltip, Typography } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"
import { useNavigate } from "react-router"
import { useLocation } from "react-router-dom"

import TourComponent from "../../Components/Tour"
import { useExternalDemoMode } from "../../hooks/useExternalDemoMode"
import NavCreators from "../../Redux/NavRedux"
import { isOnboardingFinished } from "../../Utils/onboarding/isOnboardingFinished"
import { highAccessRoles } from "../../Utils/permission"
import { getTourEl } from "../../Utils/utils"
import AlertsCreators from "../AlertsPage/reducer"
import CreditsCreators from "../CreditsPage/reducer"
import AuthCreators from "../LoginPage/reducer"
import MenuComponent from "../User"
import { useDemoDataToggle } from "../User/hooks/useDemoDataToggle"
import UserCreators from "../User/reducer"
import actions from "./actions"
import Banners from "./components/Banners"
import { navIconsArr, NavLinkWithIcon } from "./navigation"
// import { sendExtensionMessage } from '../../Utils/extension';
import SidebarHeader from "./SidebarHeader"
import { TopSideBarMenu } from "./TopSidebarMenu"

import "./styles.less"

const { Text } = Typography

const SidebarComponent = ({
  nav,
  isAdmin,
  changeSizeSidebar,
  openSidebar,
  userInfo,
  businessAccountsFetching,
  businessAccountDeleting,
  businessAccountsWithRole,
  businessAccounts,
  businessAccount,
  nextCreateAccountStep,
  demoTourStep,
  // demoRef,
  saveFetching,
  saveResult,
  alertsStatistic,
  creditsStatistic,
  balance,
  topMargin,
  featureFlag,
  ...props
}) => {
  const { isShowMenu, isShowSideBar, theme, isMobile, lastNotSettingUrl } = nav
  const closeMobileSidebar = () => props.setMobileMenuOpen(false)
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const externalDemoMode = useExternalDemoMode()

  const [activeKey, setActiveKey] = useState([])
  const activeKeyCalculated = useMemo(() => {
    if (userInfo?.is_demo && demoTourStep) return ["alerts"]
    return activeKey
  }, [activeKey, location, userInfo, demoTourStep])
  const [menu, setMenu] = useState("desktop")
  // const [showExtensionBtn, setShowExtensionBtn] = useState(false);

  const isActive = (path) => {
    return !location.pathname.includes(path)
  }

  const checkFeature = (flag) => flag && userInfo?.[flag] === false
  const onboardingFinished = isOnboardingFinished(userInfo)

  const menuIcons = useMemo(
    () =>
      navIconsArr[menu]
        ?.filter(({ featureFlag }) => !checkFeature(featureFlag))
        ?.filter(
          ({ url }) =>
            !(
              (onboardingFinished || userInfo?.is_created_by_invitation) &&
              url === "/getting_started"
            )
        )
        ?.map((menuItem) => {
          if (menuItem?.url === "/health/overview") {
            return {
              ...menuItem,
              options: menuItem?.options?.map((option) => {
                if (option?.url === "/health/monitoring") {
                  return {
                    ...option,
                    isDisabled: !userInfo?.is_business_health_allowed
                  }
                }
                return option
              })
            }
          }
          return menuItem
        }),
    [
      menu,
      onboardingFinished,
      userInfo?.is_created_by_invitation,
      userInfo?.is_business_health_allowed
    ]
  )
  const menuIconsBottom = externalDemoMode
    ? navIconsArr["bottomMenu"]?.filter((menuItem) => menuItem.text !== "Support")
    : navIconsArr["bottomMenu"]

  const siderBarStatistics = {
    alerts: alertsStatistic?.all,
    credits: creditsStatistic?.all
  }

  const hasHighAccessRole =
    businessAccountsWithRole?.some(({ role }) => highAccessRoles?.includes(role?.id)) ||
    businessAccountsWithRole.length === 0

  const isHighAccessRoleBA = !userInfo?.ba_role || highAccessRoles?.includes(userInfo?.ba_role?.id)

  const updateSidebarStatistics = () => {
    props.getAlertsStatistic()
    props.getCreditsStatistic()
  }

  const { isShowDemoData } = useDemoDataToggle()

  useEffect(() => {
    if (onboardingFinished && saveResult) {
      updateSidebarStatistics()
    }
  }, [onboardingFinished, saveResult])

  useEffect(() => {
    updateSidebarStatistics()
  }, [isShowDemoData])

  useEffect(() => {
    if (isMobile && isShowMenu) {
      closeMobileSidebar()
    }
  }, [location.pathname])

  const renderMenuIcons = (menu) => {
    return menu.map(
      ({
        url,
        withoutUrl,
        iconDesk,
        iconNode,
        text,
        tooltipText,
        comingSoon,
        markText,
        withDivider,
        markTextBordered,
        dropdown,
        options,
        flag,
        isNew,
        isNotClickable,
        isCustomAction,
        onClick,
        requireHighAccessRole,
        requireCurBAHighAccessRole,
        disabledTooltip,
        iconStyle,
        tourGuideId,
        featureFlag
      }) => {
        const isDisabled =
          isNotClickable ||
          (!hasHighAccessRole && requireHighAccessRole) ||
          (!isHighAccessRoleBA && requireCurBAHighAccessRole)
        const effectiveIsOpened = isMobile ? true : isShowSideBar
        const tooltip =
          (isDisabled && disabledTooltip) || (!effectiveIsOpened ? tooltipText || text : null)
        const navIcon = (
          <Tooltip
            placement={disabledTooltip && effectiveIsOpened ? "bottom" : "right"}
            title={tooltip}
            key={url}
            overlayClassName="sidebar-tooltip"
          >
            <div
              className={cls("nav-item-wrapper")}
              key={url}
              style={!effectiveIsOpened ? { margin: "0 auto", width: 36 } : {}}
            >
              <NavLinkWithIcon
                url={url}
                withoutUrl={withoutUrl || isDisabled}
                theme={theme}
                text={text}
                active={isActive(url)}
                iconType={iconDesk}
                isOpened={effectiveIsOpened}
                iconNode={iconNode}
                isSoon={comingSoon}
                isNew={isNew}
                markText={markText ? t(markText) : null}
                markTextBordered={markTextBordered}
                location={location.pathname}
                isDropdown={dropdown}
                options={options}
                activeKey={activeKeyCalculated}
                setActiveKey={setActiveKey}
                flag={flag}
                t={t}
                isNotClickable={isDisabled}
                isCustomAction={isCustomAction}
                onClick={onClick}
                siderBarStatistics={siderBarStatistics}
                tourGuideId={tourGuideId}
                style={iconStyle}
                isMobile={isMobile}
              />
            </div>
          </Tooltip>
        )
        const divider = <Divider className="main-sidebar_divider" style={{ margin: "10px 0" }} />
        return withDivider ? (
          <Fragment key={url}>
            {navIcon} {divider}
          </Fragment>
        ) : (
          navIcon
        )
      }
    )
  }

  const steps = useMemo(
    () => [
      {
        title: (
          <Text type="secondary">
            Invite team members and decide their access levels to control your business
          </Text>
        ),
        target: () => getTourEl("team-nav"),
        nextButtonProps: {
          onClick: () => {
            navigate("/integrations")
          }
        }
      },
      {
        title: (
          <Text type="secondary">
            Connect with Stripe, Shopify, and more to keep everything in sync.
          </Text>
        ),
        target: () => getTourEl("integrations-nav")
      },
      {
        title: <Text type="secondary">Need help? Reach out to us anytime.</Text>,
        target: () => getTourEl("support-nav")
      },
      {
        title: <Text type="secondary">Browse guides and FAQs when you want quick answers.</Text>,
        target: () => getTourEl("help-nav")
      },
      {
        title: (
          <Text type="secondary">Update your info, preferences, and security stuff here.</Text>
        ),
        target: () => getTourEl("account-settings"),
        nextButtonProps: {
          onClick: () => {
            navigate("/dashboard")
            props.setDemoTourStep(6)
          },
          children: <text>Next</text>
        }
      }
    ],
    []
  )

  return (
    <>
      {isMobile && (
        <div
          className={cls("main-sidebar_shadow", { active: isShowMenu })}
          onClick={closeMobileSidebar}
        />
      )}
      <aside
        className={cls("main-sidebar", {
          "big-sidebar": isShowSideBar && !isMobile,
          "closed-sidebar": isMobile && !isShowMenu,
          "opened-sidebar": isMobile && isShowMenu
        })}
        style={{
          overflow: demoTourStep ? "hidden" : "auto",
          top: topMargin ?? 0,
          minHeight: topMargin ? `calc(100vh - ${topMargin}px)` : "100vh",
          maxHeight: topMargin ? `calc(100vh - ${topMargin}px)` : "100vh"
        }}
        id="main-sidebar"
      >
        <div className="main-sidebar_wrap">
          <SidebarHeader
            changeSizeSidebar={changeSizeSidebar}
            isShowSideBar={isShowSideBar}
            isMobile={isMobile}
            closeMobileSidebar={closeMobileSidebar}
          />
          {(onboardingFinished || userInfo?.is_created_by_invitation) && (
            <TopSideBarMenu
              changeNextCreateAccountStep={props.changeNextCreateAccountStep}
              changeBusinessAccount={props.changeBusinessAccount}
              getBusinessAccountsWithRole={props.getBusinessAccountsWithRole}
              getBusinessAccounts={props.getBusinessAccounts}
              getUser={props.getUser}
              userSaveResultReset={props.userSaveResultReset}
              setRedirectAfterCreateAccount={props.setRedirectAfterCreateAccount}
              isMobile={isMobile}
            />
          )}
          {/* <div className='get-started-block'>
              <Progress type="circle" percent={25} size={20} style={{transform: 'rotate(180deg)'}} />
              <Spin indicator={<LoadingOutlined spin />}/>
              <LoadingOutlined />
              {isShowSideBar && <Text type="secondary" style={{fontSize: 14}}>Getting started</Text>}
            </div> */}
          <div style={{ marginTop: "16px" }}></div>
          {renderMenuIcons(menuIcons)}
        </div>
        <div className="main-sidebar_bottom">
          {renderMenuIcons(menuIconsBottom)}
          {(isShowSideBar || isMobile) && <Banners />}
          {!isMobile && <MenuComponent type={"sidebar-bottom-menu"} />}

          {/* {props.children} */}
        </div>
        <TourComponent
          open={demoTourStep === 5}
          steps={steps}
          onClose={() => props.setDemoTourStep(null)}
        />
      </aside>
    </>
  )
}

const mapStateToProps = (state) => ({
  nav: state.nav,
  isAdmin: state.auth.isAdmin,
  userInfo: state.auth.userInfo,

  saveFetching: state.user.saveFetching,
  saveResult: state.user.saveResult,
  nextCreateAccountStep: state.user.nextCreateAccountStep,

  businessAccountsFetching: state.user.businessAccountsWithRoleFetching,
  businessAccountsWithRole: state.user.businessAccountsWithRole,
  businessAccounts: state.user.businessAccounts,
  businessAccount: state.user.businessAccount,
  alertsStatistic: state.alerts?.statistics,
  creditsStatistic: state.credits?.statistics,
  balance: state.balance.balance,
  demoTourStep: state.user.demoTourStep,
  isMobile: state.nav.isMobile
})

const mapDispatchToProps = (dispatch) => ({
  changeSizeSidebar: (value) => dispatch(actions.changeSizeSidebar("isShowSideBar", value)),
  setMobileMenuOpen: (value) => dispatch(NavCreators.navSetProp("isShowMenu", value)),
  logout: () => dispatch(AuthCreators.logout()),
  getBusinessAccounts: () => dispatch(UserCreators.getBusinessAccountsRequest()),
  getBusinessAccountsWithRole: () => dispatch(UserCreators.getBusinessAccountsWithRoleRequest()),
  changeBusinessAccount: (newBusinessAccountId) =>
    dispatch(UserCreators.userSaveRequest({ current_business_account_id: newBusinessAccountId })),
  userSaveResultReset: () => dispatch(UserCreators.userSaveResultReset()),
  getUser: () => dispatch(UserCreators.userRequest()),
  changeNextCreateAccountStep: (nextCreateAccountStep) =>
    dispatch(UserCreators.changeNextCreateAccountStep(nextCreateAccountStep)),
  setRedirectAfterCreateAccount: (location) =>
    dispatch(UserCreators.userSetProp("redirectAfterCreateAccount", location)),
  setDemoTourStep: (step) => dispatch(UserCreators.setDemoTourStep(step)),
  getAlertsStatistic: () => dispatch(AlertsCreators.getAlertsStatisticsRequest()),
  getCreditsStatistic: () => dispatch(CreditsCreators.getCreditsStatisticsRequest())
})

export default connect(mapStateToProps, mapDispatchToProps)(SidebarComponent)
